import { Component, EventEmitter, HostListener, Input, Output, ViewChildren, QueryList } from '@angular/core';
import { WizardPageComponent } from '../../../../../../core/wizard/wizard-page-component';
import type { SalesVolumeRangeFilterWizard } from './sales-volume-range-filter-wizard';
import { GeoFilterComponent } from './geo-filter/geo-filter.component';
import { CompanyFiltersComponent } from './company-filters/company-filters.component';
import { SqftFiltersComponent } from './sqft-filters/sqft-filters.component';
import { SalesVolumeRangeApiService } from './sales-volume-range-api.service';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { Store } from '../../../../../../core/models';
import { Store as NgrxStore } from '@ngrx/store';
import { AppState } from '../../../../../../app-state';
import { selectDetailState } from '../../../../../detail-state';

export interface SalesVolumeRangeData {
  minValue: number;
  maxValue: number;
  selectedCategory: string;
}

export type FilterCategoryKey = 'sales-volume' | 'geography' | 'geography-states' | 'geography-counties' | 'geography-msas' | 'geography-zipCodes' | 'company' | 'sqft';

export interface FilterSection {
  key: FilterCategoryKey;
  name: string;
  icon: string;
  count: number;
  filters: any[];
}

export interface UniversalFilterData {
  salesVolume: {
    range: { minValue: number; maxValue: number; } | null;
  };
  geography: {
    states: string[];
    stateLabels?: string[];
    // States selected that the company does NOT subscribe to directly, but which contain
    // subscribed CBSAs. These are sent to the API as their CBSAs (derivedCbsas), never as states.
    derivedStates?: string[];
    derivedStateLabels?: string[];
    derivedCbsas?: string[];
    counties: string[];
    msas: string[];
    msaLabels?: string[];
    zipCodes: string[];
    driveTime?: number;
  };
  company: {
    banners: string[];
    bannerLabels?: string[];
  };
  sqft: {
    range: { minValue: number; maxValue: number; } | null;
  };
}

@Component({
  standalone: false,
  selector: 'mtn-sales-volume-range-filter-dialog',
  templateUrl: './sales-volume-range-filter-dialog.component.html',
  styleUrls: ['./sales-volume-range-filter-dialog.component.scss']
})
export class SalesVolumeRangeFilterDialogComponent extends WizardPageComponent<SalesVolumeRangeFilterWizard> {
  key = 'sales-volume-range-filter';

  @Input() isVisible = false;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() apply = new EventEmitter<UniversalFilterData>();
  @Output() reset = new EventEmitter<void>();

  @ViewChildren(GeoFilterComponent) geoFilterComponents!: QueryList<GeoFilterComponent>;
  @ViewChildren(CompanyFiltersComponent) companyFilterComponents!: QueryList<CompanyFiltersComponent>;
  @ViewChildren(SqftFiltersComponent) sqftFilterComponents!: QueryList<SqftFiltersComponent>;

  // Universal filter data object (applied filters)
  filterData: UniversalFilterData = {
    salesVolume: { range: null },
    geography: { states: [], counties: [], msas: [], zipCodes: [], driveTime: undefined },
    company: { banners: [] },
    sqft: { range: null }
  };

  // Temporary filter data (pending selections)
  pendingFilterData: UniversalFilterData = {
    salesVolume: { range: null },
    geography: { states: [], counties: [], msas: [], zipCodes: [], driveTime: undefined },
    company: { banners: [] },
    sqft: { range: null }
  };

  // Filter sections configuration
  filterSections: FilterSection[] = [
    { key: 'sales-volume', name: 'Sales Volume', icon: 'chart', count: 0, filters: [] },
    { key: 'geography', name: 'Geography', icon: 'calendar', count: 0, filters: [] },
    { key: 'company', name: 'FIT', icon: 'user', count: 0, filters: [] },
    { key: 'sqft', name: 'SQFT', icon: 'location', count: 0, filters: [] }
  ];

  // Selected category
  selectedCategory: any = 'sales-volume';

  // Sales Volume Range specific properties
  rangeMin = 0;
  rangeMax = 0;
  minValue = 0;
  maxValue = 0;
  minInputValue = 0;
  maxInputValue = 0;
  hasMinError = false;
  hasMaxError = false;

  // SQFT range bounds from API
  sqftRangeMin: number | null = null;
  sqftRangeMax: number | null = null;

  // Loading state
  isLoadingRange = false;

  // Initial values for comparison
  private initialValues = {
    salesVolume: { minValue: 0, maxValue: 0 },
    geography: { states: [], counties: [] },
    company: { banners: [] },
    sqft: { minValue: 0, maxValue: 0 }
  };

  // Store original API values separately
  private originalApiValues = {
    sqft: { minValue: 0, maxValue: 0 }
  };

  // Drag state
  private isDragging = false;
  private dragHandle: 'min' | 'max' | null = null;

  // Flag to skip selection change handlers during reset
  private isResetting = false;

  // Current store from state
  store: Store | null = null;

  constructor(private salesVolumeRangeApiService: SalesVolumeRangeApiService, 
              private hitTrackingService: HitTrackingService,
              protected ngrxStore: NgrxStore<AppState>) {
    super();
    this.title = 'Filter Sales Volume';
    this.closeButtonText = 'Cancel';
  }

  onLoad() {
    if (this.wizard?.model?.filterData) {
      this.filterData = JSON.parse(JSON.stringify(this.wizard.model.filterData));
    }
    if (this.wizard?.model?.startingCategory) {
      this.selectedCategory = this.wizard.model.startingCategory;
    }
    
    // Subscribe to store state
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state) => {
          this.store = state.store;
        })
    );
    
    this.fetchRangeBounds();
    return super.onLoad();
  }

  private fetchRangeBounds(): void {
    this.isLoadingRange = true;
    this.salesVolumeRangeApiService.getSalesVolumeRange().subscribe({
      next: (response) => {
        this.rangeMin = response.minVolumeTotal;
        this.rangeMax = response.maxVolumeTotal;
        this.initialValues.salesVolume = {
          minValue: response.minVolumeTotal,
          maxValue: response.maxVolumeTotal
        };
        this.initialValues.sqft = {
          minValue: response.minSalesSqftByTotalArea,
          maxValue: response.maxSalesSqftByTotalArea
        };
        // Store original API values
        this.originalApiValues.sqft = {
          minValue: response.minSalesSqftByTotalArea,
          maxValue: response.maxSalesSqftByTotalArea
        };
        this.sqftRangeMin = response.minSalesSqftByTotalArea;
        this.sqftRangeMax = response.maxSalesSqftByTotalArea;

        if (this.wizard?.model?.filterData) {
          this.syncPendingFromApplied();
        } else {
          this.minValue = response.minVolumeTotal;
          this.maxValue = response.maxVolumeTotal;
          this.minInputValue = response.minVolumeTotal;
          this.maxInputValue = response.maxVolumeTotal;
          this.filterData.salesVolume.range = {
            minValue: response.minVolumeTotal,
            maxValue: response.maxVolumeTotal
          };
          this.syncPendingFromApplied();
        }

        this.updatePendingFilterCounts();
        this.isLoadingRange = false;
      },
      error: () => {
        this.isLoadingRange = false;
      }
    });
  }

  /**
   * Syncs pendingFilterData from filterData (applied filters).
   * Call this when opening the dialog to show current applied state.
   */
  syncPendingFromApplied(): void {
    // Deep copy applied filters to pending
    this.pendingFilterData.salesVolume.range = this.filterData.salesVolume.range
      ? { ...this.filterData.salesVolume.range }
      : { minValue: this.initialValues.salesVolume.minValue, maxValue: this.initialValues.salesVolume.maxValue };

    this.pendingFilterData.geography = {
      states: [...this.filterData.geography.states],
      stateLabels: [...(this.filterData.geography.stateLabels || [])],
      derivedStates: [...(this.filterData.geography.derivedStates || [])],
      derivedStateLabels: [...(this.filterData.geography.derivedStateLabels || [])],
      derivedCbsas: [...(this.filterData.geography.derivedCbsas || [])],
      counties: [...this.filterData.geography.counties],
      msas: [...this.filterData.geography.msas],
      msaLabels: [...(this.filterData.geography.msaLabels || [])],
      zipCodes: [...this.filterData.geography.zipCodes],
      driveTime: this.filterData.geography.driveTime
    };

    this.pendingFilterData.company = {
      banners: [...this.filterData.company.banners]
    };

    this.pendingFilterData.sqft = this.filterData.sqft.range
      ? { range: { ...this.filterData.sqft.range } }
      : { range: null };

    // Check if Club or Supercenter is selected and update SQFT range accordingly
    const hasClubOrSupercenter = this.pendingFilterData.company.banners.some(company => 
      company.toLowerCase().includes('club') || company.toLowerCase().includes('supercenter')
    );
    
    if (hasClubOrSupercenter) {
      // Update external range values that control the dragger labels
      this.sqftRangeMin = 0.42;
      this.sqftRangeMax = 279;
      // Update initial values for Club/Supercenter range
      this.initialValues.sqft = { minValue: 0.42, maxValue: 279 };
      // Update SQFT filter component with new range
      setTimeout(() => {
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(0.42, 279, 
            this.pendingFilterData.sqft.range?.minValue || 0.42, 
            this.pendingFilterData.sqft.range?.maxValue || 279);
        });
      });
    } else {
      // Reset to original API values
      const originalMin = this.originalApiValues.sqft.minValue;
      const originalMax = this.originalApiValues.sqft.maxValue;
      this.sqftRangeMin = originalMin;
      this.sqftRangeMax = originalMax;
      this.initialValues.sqft = { minValue: originalMin, maxValue: originalMax };
      
      // If SQFT filter is applied, adjust values to fit within original bounds
      if (this.pendingFilterData.sqft.range) {
        const currentMin = this.pendingFilterData.sqft.range.minValue;
        const currentMax = this.pendingFilterData.sqft.range.maxValue;
        
        // Clamp values to original bounds
        const adjustedMin = Math.max(originalMin, Math.min(currentMin, originalMax));
        const adjustedMax = Math.max(originalMin, Math.min(currentMax, originalMax));
        
        // Ensure min is not greater than max
        const finalMin = Math.min(adjustedMin, adjustedMax);
        const finalMax = Math.max(adjustedMin, adjustedMax);
        
        this.pendingFilterData.sqft.range = {
          minValue: finalMin,
          maxValue: finalMax
        };
        
        // Update SQFT filter component with adjusted values
        setTimeout(() => {
          this.sqftFilterComponents?.forEach(component => {
            component.updateRangeAndPositions(originalMin, originalMax, finalMin, finalMax);
          });
        });
      } else {
        // Update SQFT filter component with original range
        setTimeout(() => {
          this.sqftFilterComponents?.forEach(component => {
            component.updateRangeAndPositions(originalMin, originalMax, originalMin, originalMax);
          });
        });
      }
    }

    // Update UI values for sales volume
    if (this.pendingFilterData.salesVolume.range) {
      this.minValue = this.pendingFilterData.salesVolume.range.minValue;
      this.maxValue = this.pendingFilterData.salesVolume.range.maxValue;
      this.minInputValue = this.pendingFilterData.salesVolume.range.minValue;
      this.maxInputValue = this.pendingFilterData.salesVolume.range.maxValue;
    }
  }

  /**
   * Updates filterSections counts based on pendingFilterData.
   * This is the primary method for updating sidebar badge counts.
   * Note: getAppliedFilters() reads directly from filterData for the "Applied Filters" section.
   */
  private updateFilterCounts(): void {
    // Delegate to pending counts - sidebar always shows pending/working state
    this.updatePendingFilterCounts();
  }

  getAppliedFilters(): FilterSection[] {
    // Return filters based on pendingFilterData to show live updates as user selects filters
    const appliedFilters: FilterSection[] = [];

    // Check sales volume from pending data
    const salesRange = this.pendingFilterData.salesVolume.range;
    if (salesRange &&
        (salesRange.minValue !== this.initialValues.salesVolume.minValue ||
         salesRange.maxValue !== this.initialValues.salesVolume.maxValue)) {
      appliedFilters.push({ key: 'sales-volume', name: 'Sales Volume', icon: 'chart', count: 1, filters: [] });
    }

    // Check geography from pending data
    const geoCount = this.pendingFilterData.geography.states.length +
                     (this.pendingFilterData.geography.derivedStates?.length || 0) +
                     this.pendingFilterData.geography.counties.length +
                     this.pendingFilterData.geography.msas.length +
                     this.pendingFilterData.geography.zipCodes.length +
                     (this.pendingFilterData.geography.driveTime ? 1 : 0);
    if (geoCount > 0) {
      appliedFilters.push({ key: 'geography', name: 'Geography', icon: 'calendar', count: geoCount, filters: [] });
    }

    // Check company from pending data
    if (this.pendingFilterData.company.banners.length > 0) {
      appliedFilters.push({ key: 'company', name: 'FIT', icon: 'user', count: this.pendingFilterData.company.banners.length, filters: [] });
    }

    // Check sqft from pending data
    const sqftRange = this.pendingFilterData.sqft.range;
    if (sqftRange &&
        (sqftRange.minValue !== this.initialValues.sqft.minValue ||
         sqftRange.maxValue !== this.initialValues.sqft.maxValue)) {
      appliedFilters.push({ key: 'sqft', name: 'SQFT', icon: 'location', count: 1, filters: [] });
    }

    return appliedFilters;
  }

  getSelectedSectionName(): string {
    const section = this.filterSections.find(s => s.key === this.selectedCategory);
    return section ? section.name : 'Sales Volume';
  }

  getFilterTitle(): string {
    return `Filter ${this.getSelectedSectionName()} by`;
  }

  getContentTitle(): string {
    return `${this.getSelectedSectionName()} Filters`;
  }

  selectCategory(category: FilterCategoryKey): void {
    this.selectedCategory = category;
  }

  isGeographySelected(): boolean {
    return this.selectedCategory === 'geography';
  }

  // Range slider methods
  getMinHandlePosition(): number {
    return ((this.minValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
  }

  getMaxHandlePosition(): number {
    return ((this.maxValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
  }

  getProgressLeft(): number {
    return this.getMinHandlePosition();
  }

  getProgressWidth(): number {
    return this.getMaxHandlePosition() - this.getMinHandlePosition();
  }

  startDrag(handle: 'min' | 'max', event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragHandle = handle;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.dragHandle) return;

    const slider = document.querySelector('.slider-wrapper') as HTMLElement;
    if (!slider) return;

    const rect = slider.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const value = this.rangeMin + (percentage / 100) * (this.rangeMax - this.rangeMin);

    if (this.dragHandle === 'min') {
      this.minValue = Math.min(value, this.maxValue - 100000);
      this.minInputValue = this.minValue;
    } else {
      this.maxValue = Math.max(value, this.minValue + 100000);
      this.maxInputValue = this.maxValue;
    }

    this.validateInputs();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isDragging) {
      this.updateFilterDataAndCounts();
    }
    this.isDragging = false;
    this.dragHandle = null;
  }

  // Input handling
  onMinInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filteredValue = this.filterNumericInput(target.value);
    target.value = filteredValue;
    const value = this.parseInputValue(filteredValue);
    if (!isNaN(value)) {
      this.minInputValue = value;
      this.minValue = value;
      this.validateInputs();
      this.updateFilterDataAndCounts();
    }
  }

  onMaxInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filteredValue = this.filterNumericInput(target.value);
    target.value = filteredValue;
    const value = this.parseInputValue(filteredValue);
    if (!isNaN(value)) {
      this.maxInputValue = value;
      this.maxValue = value;
      this.validateInputs();
      this.updateFilterDataAndCounts();
    }
  }

  private filterNumericInput(value: string): string {
    return value.replace(/[^0-9,]/g, '');
  }

  private updateFilterDataAndCounts(): void {
    this.pendingFilterData.salesVolume.range = {
      minValue: this.minInputValue,
      maxValue: this.maxInputValue
    };
    this.updatePendingFilterCounts();
  }

  private updatePendingFilterCounts(): void {
    this.filterSections.forEach(section => {
      switch (section.key) {
        case 'sales-volume':
          const salesRange = this.pendingFilterData.salesVolume.range;
          const isChanged = salesRange &&
            (salesRange.minValue !== this.initialValues.salesVolume.minValue ||
             salesRange.maxValue !== this.initialValues.salesVolume.maxValue);
          section.count = isChanged ? 1 : 0;
          break;
        case 'geography':
          let pendingGeoCount = this.pendingFilterData.geography.states.length +
                         (this.pendingFilterData.geography.derivedStates?.length || 0) +
                         this.pendingFilterData.geography.counties.length +
                         this.pendingFilterData.geography.msas.length +
                         this.pendingFilterData.geography.zipCodes.length;
          // Add 1 if drive time is set
          if (this.pendingFilterData.geography.driveTime !== undefined && this.pendingFilterData.geography.driveTime > 0) {
            pendingGeoCount += 1;
          }
          section.count = pendingGeoCount;
          break;
        case 'company':
          section.count = this.pendingFilterData.company.banners.length;
          break;
        case 'sqft':
          const sqftRange = this.pendingFilterData.sqft.range;
          const isSqftChanged = sqftRange &&
            (sqftRange.minValue !== this.initialValues.sqft.minValue ||
             sqftRange.maxValue !== this.initialValues.sqft.maxValue);
          section.count = isSqftChanged ? 1 : 0;
          break;
      }
    });
  }

  validateInputs(): void {
    this.hasMinError = this.minInputValue < this.rangeMin || this.minInputValue > this.rangeMax;
    this.hasMaxError = this.maxInputValue < this.rangeMin || this.maxInputValue > this.rangeMax || this.maxInputValue <= this.minInputValue;
  }

  // Formatting utilities
  formatNumber(value: number): string {
    if (value >= 1000000) {
      const millions = (value / 1000000).toFixed(1);
      return millions.endsWith('.0') ? `${Math.floor(value / 1000000)}M` : `${millions}M`;
    }
    if (value >= 1000) {
      return `${Math.floor(value / 1000)}K`;
    }
    return value.toLocaleString();
  }

  formatInputValue(value: number): string {
    return Math.floor(value).toLocaleString('en-US');
  }

  parseInputValue(value: string): number {
    return parseInt(value.replace(/,/g, ''), 10);
  }

  // Event handlers
  onLocalReset(): void {
    // Build previous filters object with only applied/changed filters
    const previousFilters: any = {};
    
    // Sales Volume - only include if different from initial values
    if (this.filterData.salesVolume?.range && 
        (this.filterData.salesVolume.range.minValue !== this.initialValues.salesVolume.minValue ||
         this.filterData.salesVolume.range.maxValue !== this.initialValues.salesVolume.maxValue)) {
      previousFilters.salesVolume = {
        minValue: this.filterData.salesVolume.range.minValue,
        maxValue: this.filterData.salesVolume.range.maxValue
      };
    }
    
    // Geography - only include if there are selections
    if (this.filterData.geography && 
        (this.filterData.geography.states?.length > 0 ||
         this.filterData.geography.counties?.length > 0 ||
         this.filterData.geography.msas?.length > 0 ||
         this.filterData.geography.zipCodes?.length > 0 ||
         this.filterData.geography.driveTime)) {
      previousFilters.geography = {
        states: this.filterData.geography.states || [],
        counties: this.filterData.geography.counties || [],
        msas: this.filterData.geography.msas || [],
        zipCodes: this.filterData.geography.zipCodes || [],
        driveTime: this.filterData.geography.driveTime
      };
    }
    
    // Company - only include if there are banner selections
    if (this.filterData.company?.banners?.length > 0) {
      previousFilters.company = {
        banners: this.filterData.company.banners
      };
    }
    
    // SQFT - only include if different from initial values
    if (this.filterData.sqft?.range &&
        (this.filterData.sqft.range.minValue !== this.initialValues.sqft.minValue ||
         this.filterData.sqft.range.maxValue !== this.initialValues.sqft.maxValue)) {
      previousFilters.sqft = {
        minValue: this.filterData.sqft.range.minValue,
        maxValue: this.filterData.sqft.range.maxValue
      };
    }

    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      bannerName: this.store?.banner?.name,
      category: this.selectedCategory === 'company' ? 'Fit' : this.selectedCategory,
      previousFilters: Object.keys(previousFilters).length > 0 ? previousFilters : null,
    };

    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_FILTER_RESET_CATEGORY,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_FILTER_RESET_CATEGORY],
        true
      ).subscribe({ error: () => {} });
    // Only reset pendingFilterData - filterData should remain unchanged until Apply is clicked
    if (this.selectedCategory === 'sales-volume') {
      this.minValue = this.initialValues.salesVolume.minValue;
      this.maxValue = this.initialValues.salesVolume.maxValue;
      this.minInputValue = this.initialValues.salesVolume.minValue;
      this.maxInputValue = this.initialValues.salesVolume.maxValue;
      this.pendingFilterData.salesVolume.range = {
        minValue: this.initialValues.salesVolume.minValue,
        maxValue: this.initialValues.salesVolume.maxValue
      };
      this.hasMinError = false;
      this.hasMaxError = false;
    } else if (this.selectedCategory === 'geography') {
      // Set flag to prevent selection change handler from overwriting reset values
      this.isResetting = true;
      this.pendingFilterData.geography = { states: [], counties: [], msas: [], zipCodes: [], driveTime: undefined };
      this.geoFilterComponents?.forEach(component => component.resetSelections());
      this.isResetting = false;
    } else if (this.selectedCategory === 'company') {
      this.isResetting = true;
      this.pendingFilterData.company = { banners: [] };
      this.companyFilterComponents?.forEach(component => component.resetSelections());
      
      // When company filter is reset, update SQFT to original values
      const originalMin = this.originalApiValues.sqft.minValue;
      const originalMax = this.originalApiValues.sqft.maxValue;
      this.sqftRangeMin = originalMin;
      this.sqftRangeMax = originalMax;
      this.initialValues.sqft = { minValue: originalMin, maxValue: originalMax };
      
      // If SQFT filter is applied, adjust values to fit within original bounds
      if (this.pendingFilterData.sqft.range) {
        const currentMin = this.pendingFilterData.sqft.range.minValue;
        const currentMax = this.pendingFilterData.sqft.range.maxValue;
        
        // Clamp values to original bounds
        const adjustedMin = Math.max(originalMin, Math.min(currentMin, originalMax));
        const adjustedMax = Math.max(originalMin, Math.min(currentMax, originalMax));
        
        // Ensure min is not greater than max
        const finalMin = Math.min(adjustedMin, adjustedMax);
        const finalMax = Math.max(adjustedMin, adjustedMax);
        
        this.pendingFilterData.sqft.range = {
          minValue: finalMin,
          maxValue: finalMax
        };
        
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(originalMin, originalMax, finalMin, finalMax);
        });
      } else {
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(originalMin, originalMax, originalMin, originalMax);
        });
      }
      
      this.isResetting = false;
    } else if (this.selectedCategory === 'sqft') {
      this.isResetting = true;
      this.pendingFilterData.sqft.range = null;
      this.sqftFilterComponents?.forEach(component => component.resetSelections());
      // Check if Club or Supercenter is still selected and reset to appropriate values
      const hasClubOrSupercenter = this.pendingFilterData.company.banners.some(company => 
        company.toLowerCase().includes('club') || company.toLowerCase().includes('supercenter')
      );
      if (hasClubOrSupercenter) {
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(0.42, 279, 0.42, 279);
        });
      } else {
        const originalMin = this.originalApiValues.sqft.minValue;
        const originalMax = this.originalApiValues.sqft.maxValue;
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(originalMin, originalMax, originalMin, originalMax);
        });
      }
      this.isResetting = false;
    }

    // Update pending counts to reflect the reset (sidebar badges show pending state)
    this.updatePendingFilterCounts();
  }

  onReset(): void {
    // Build previous filters object with only applied/changed filters
    const previousFilters: any = {};
    
    // Sales Volume - only include if different from initial values
    if (this.filterData.salesVolume?.range && 
        (this.filterData.salesVolume.range.minValue !== this.initialValues.salesVolume.minValue ||
         this.filterData.salesVolume.range.maxValue !== this.initialValues.salesVolume.maxValue)) {
      previousFilters.salesVolume = {
        minValue: this.filterData.salesVolume.range.minValue,
        maxValue: this.filterData.salesVolume.range.maxValue
      };
    }
    
    // Geography - only include if there are selections
    if (this.filterData.geography && 
        (this.filterData.geography.states?.length > 0 ||
         this.filterData.geography.counties?.length > 0 ||
         this.filterData.geography.msas?.length > 0 ||
         this.filterData.geography.zipCodes?.length > 0 ||
         this.filterData.geography.driveTime)) {
      previousFilters.geography = {
        states: this.filterData.geography.states || [],
        counties: this.filterData.geography.counties || [],
        msas: this.filterData.geography.msas || [],
        zipCodes: this.filterData.geography.zipCodes || [],
        driveTime: this.filterData.geography.driveTime
      };
    }
    
    // Company - only include if there are banner selections
    if (this.filterData.company?.banners?.length > 0) {
      previousFilters.company = {
        banners: this.filterData.company.banners
      };
    }
    
    // SQFT - only include if different from initial values
    if (this.filterData.sqft?.range &&
        (this.filterData.sqft.range.minValue !== this.initialValues.sqft.minValue ||
         this.filterData.sqft.range.maxValue !== this.initialValues.sqft.maxValue)) {
      previousFilters.sqft = {
        minValue: this.filterData.sqft.range.minValue,
        maxValue: this.filterData.sqft.range.maxValue
      };
    }

    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      bannerName: this.store?.banner?.name,
      previous_filters: Object.keys(previousFilters).length > 0 ? previousFilters : null,
      // resultCountBefore : before_count,
      // resultCountAfter : after_count
    };

    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_FILTER_RESET_GLOBAL,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_FILTER_RESET_GLOBAL],
        true
      ).subscribe({ error: () => {} });
    // Reset to initial values, not range min/max
    this.minValue = this.initialValues.salesVolume.minValue;
    this.maxValue = this.initialValues.salesVolume.maxValue;
    this.minInputValue = this.initialValues.salesVolume.minValue;
    this.maxInputValue = this.initialValues.salesVolume.maxValue;

    // Reset both applied and pending filter data to initial state
    this.filterData.salesVolume.range = {
      minValue: this.initialValues.salesVolume.minValue,
      maxValue: this.initialValues.salesVolume.maxValue
    };
    this.filterData.geography = { states: [], counties: [], msas: [], zipCodes: [], driveTime: undefined };
    this.filterData.company = { banners: [] };
    this.filterData.sqft.range = null;

    this.pendingFilterData.salesVolume.range = {
      minValue: this.initialValues.salesVolume.minValue,
      maxValue: this.initialValues.salesVolume.maxValue
    };
    this.pendingFilterData.geography = { states: [], counties: [], msas: [], zipCodes: [], driveTime: undefined };
    this.pendingFilterData.company = { banners: [] };
    this.pendingFilterData.sqft.range = null;

    this.hasMinError = false;
    this.hasMaxError = false;

    // Set flag to prevent selection change handlers from overwriting reset values
    this.isResetting = true;

    // Reset child components
    this.geoFilterComponents?.forEach(component => component.resetSelections());
    this.companyFilterComponents?.forEach(component => component.resetSelections());
    this.sqftFilterComponents?.forEach(component => component.resetSelections());

    // Reset SQFT range to original values since company filters are cleared
    const originalMin = this.originalApiValues.sqft.minValue;
    const originalMax = this.originalApiValues.sqft.maxValue;
    this.sqftRangeMin = originalMin;
    this.sqftRangeMax = originalMax;
    this.initialValues.sqft = { minValue: originalMin, maxValue: originalMax };
    this.sqftFilterComponents?.forEach(component => {
      component.updateRangeAndPositions(originalMin, originalMax, originalMin, originalMax);
    });

    this.isResetting = false;

    // Update both applied and pending counts
    this.updateFilterCounts();
    this.updatePendingFilterCounts();
    this.reset.emit();
  }

  onCancel(): void {
    // Reset pending state back to applied state (discard unsaved changes)
    this.syncPendingFromApplied();
    this.updatePendingFilterCounts();
    this.closeEvent.emit();
    this.close();
  }

  onApply(): void {
    if (this.hasMinError || this.hasMaxError) return;

    // Get result count before applying filters
    const resultCountBefore = this.getResultCountFromParent();

    // Apply all pending selections to actual filter data (deep copy)
    this.filterData.salesVolume.range = this.pendingFilterData.salesVolume.range
      ? { ...this.pendingFilterData.salesVolume.range }
      : { minValue: this.initialValues.salesVolume.minValue, maxValue: this.initialValues.salesVolume.maxValue };

    this.filterData.geography = {
      states: [...this.pendingFilterData.geography.states],
      stateLabels: [...(this.pendingFilterData.geography.stateLabels || [])],
      derivedStates: [...(this.pendingFilterData.geography.derivedStates || [])],
      derivedStateLabels: [...(this.pendingFilterData.geography.derivedStateLabels || [])],
      derivedCbsas: [...(this.pendingFilterData.geography.derivedCbsas || [])],
      counties: [...this.pendingFilterData.geography.counties],
      msas: [...this.pendingFilterData.geography.msas],
      msaLabels: [...(this.pendingFilterData.geography.msaLabels || [])],
      zipCodes: [...this.pendingFilterData.geography.zipCodes],
      driveTime: this.pendingFilterData.geography.driveTime
    };

    this.filterData.company = {
      banners: [...this.pendingFilterData.company.banners],
      bannerLabels: [...(this.pendingFilterData.company.bannerLabels || [])]
    };

    this.filterData.sqft = this.pendingFilterData.sqft.range
      ? { range: { ...this.pendingFilterData.sqft.range } }
      : { range: null };

    // Update both counts - pending stays in sync with applied after applying
    this.updateFilterCounts();
    this.updatePendingFilterCounts();
    
    // Create a custom event to notify parent and get result counts
    const filterAppliedEvent = new CustomEvent('filterApplied', {
      detail: {
        filterData: this.filterData,
        resultCountBefore: resultCountBefore,
        callback: (resultCountAfter: number) => {
          const metadata = {
            storeId: this.store?.uuid,
            storeName: this.store?.name,
            bannerName: this.store?.banner?.name,
            appliedFilters: this.filterData,
            resultCountBefore: resultCountBefore,
            resultCountAfter: resultCountAfter
          };

          this.hitTrackingService.hitTheUserTracking(
              USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
              metadata,
              USER_TRACKING_ACTIONS.RBS_FILTER_APPLIED,
              USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_FILTER_APPLIED],
              true
            ).subscribe({ error: () => {} });
        }
      }
    });

    // Dispatch the event
    window.dispatchEvent(filterAppliedEvent);
    
    this.apply.emit(this.filterData);

    if (this.wizard?.model) {
      this.wizard.model.filterData = this.filterData;
      this.wizard.model.applied = true;
    }

    this.close();
  }

  // Event handlers for child component selection changes (store in pending)
  onGeographySelectionChange(selection: {states: string[], stateLabels?: string[], derivedStates?: string[], derivedStateLabels?: string[], derivedCbsas?: string[], counties: string[], msas: string[], msaLabels?: string[], zipCodes: string[], driveTime?: number}): void {
    // Skip if we're in the middle of a reset operation
    if (this.isResetting) return;

    this.pendingFilterData.geography = {
      states: selection.states || [],
      stateLabels: selection.stateLabels || [],
      derivedStates: selection.derivedStates || [],
      derivedStateLabels: selection.derivedStateLabels || [],
      derivedCbsas: selection.derivedCbsas || [],
      counties: selection.counties || [],
      msas: selection.msas || [],
      msaLabels: selection.msaLabels || [],
      zipCodes: selection.zipCodes || [],
      driveTime: selection.driveTime
    };
    this.updatePendingFilterCounts();
  }

  onCompanySelectionChange(selection: { banners: string[], bannerLabels: string[] }): void {
    // Skip if we're in the middle of a reset operation
    if (this.isResetting) return;

    this.pendingFilterData.company.banners = selection?.banners || [];
    this.pendingFilterData.company.bannerLabels = selection?.bannerLabels || [];
    const selectedCompanies = selection?.banners || [];
    
    // Check if Club or Supercenter is selected
    const hasClubOrSupercenter = selectedCompanies.some(company => 
      company.toLowerCase().includes('club') || company.toLowerCase().includes('supercenter')
    );
    
    if (hasClubOrSupercenter) {
      // Update external range values that control the dragger labels
      this.sqftRangeMin = 0.42;
      this.sqftRangeMax = 279;
      // Update initial values for Club/Supercenter range
      this.initialValues.sqft = { minValue: 0.42, maxValue: 279 };
      
      // If SQFT filter is applied, expand values to take advantage of Club/Supercenter bounds
      if (this.pendingFilterData.sqft.range) {
        const currentMin = this.pendingFilterData.sqft.range.minValue;
        const currentMax = this.pendingFilterData.sqft.range.maxValue;
        
        // For min: use current value if it's >= 0.42, otherwise use 0.42
        const adjustedMin = Math.max(0.42, currentMin);
        
        // For max: if current max was at the original API max, expand to new max (279)
        // Otherwise preserve user's selection if it's within new bounds
        let adjustedMax;
        if (currentMax === this.originalApiValues.sqft.maxValue) {
          // User had max at original limit, expand to new limit
          adjustedMax = 279;
        } else {
          // User had custom max value, preserve it but ensure it's within new bounds
          adjustedMax = Math.max(0.42, Math.min(currentMax, 279));
        }
        
        // Ensure min is not greater than max
        const finalMin = Math.min(adjustedMin, adjustedMax);
        const finalMax = Math.max(adjustedMin, adjustedMax);
        
        this.pendingFilterData.sqft.range = {
          minValue: finalMin,
          maxValue: finalMax
        };
        
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(0.42, 279, finalMin, finalMax);
        });
      } else {
        // No SQFT filter applied, just update range bounds
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(0.42, 279, 0.42, 279);
        });
      }
    } else {
      // Reset external range values to original API values
      const originalMin = this.originalApiValues.sqft.minValue;
      const originalMax = this.originalApiValues.sqft.maxValue;
      this.sqftRangeMin = originalMin;
      this.sqftRangeMax = originalMax;
      // Reset initial values to original
      this.initialValues.sqft = { minValue: originalMin, maxValue: originalMax };
      
      // If SQFT filter is currently applied, adjust values to fit within original bounds
      if (this.pendingFilterData.sqft.range) {
        const currentMin = this.pendingFilterData.sqft.range.minValue;
        const currentMax = this.pendingFilterData.sqft.range.maxValue;
        
        // Clamp values to original bounds
        const adjustedMin = Math.max(originalMin, Math.min(currentMin, originalMax));
        const adjustedMax = Math.max(originalMin, Math.min(currentMax, originalMax));
        
        // Ensure min is not greater than max
        const finalMin = Math.min(adjustedMin, adjustedMax);
        const finalMax = Math.max(adjustedMin, adjustedMax);
        
        this.pendingFilterData.sqft.range = {
          minValue: finalMin,
          maxValue: finalMax
        };
        
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(originalMin, originalMax, finalMin, finalMax);
        });
      } else {
        this.sqftFilterComponents?.forEach(component => {
          component.updateRangeAndPositions(originalMin, originalMax, originalMin, originalMax);
        });
      }
    }
    
    this.updatePendingFilterCounts();
  }

  onSqftSelectionChange(range: {minValue: number, maxValue: number} | null): void {
    // Skip if we're in the middle of a reset operation
    if (this.isResetting) return;

    this.pendingFilterData.sqft.range = range;
    this.updatePendingFilterCounts();
  }

  // Get total active filter count
  getTotalFilterCount(): number {
    return this.filterSections.reduce((total, section) => total + section.count, 0);
  }

  // Check if any filters are applied
  hasActiveFilters(): boolean {
    return this.getTotalFilterCount() > 0;
  }

  // Get result count from parent component (comprehensive ranking table)
  private getResultCountFromParent(): number {
    // Try to get the result count from the parent component
    // This assumes the parent component stores the total items count
    try {
      // Access parent component through wizard model or global state
      if (this.wizard?.model?.parentComponent) {
        return this.wizard.model.parentComponent.totalItems || 0;
      }
      
      // Alternative: try to find the parent component in the DOM
      const parentElement = document.querySelector('mtn-comprehensive-ranking-table-page');
      if (parentElement && (parentElement as any).componentInstance) {
        return (parentElement as any).componentInstance.totalItems || 0;
      }
      
      // Fallback: try to get from window/global scope if parent exposes it
      if ((window as any).comprehensiveRankingTableData) {
        return (window as any).comprehensiveRankingTableData.totalItems || 0;
      }
      
      return 0;
    } catch (error) {
      console.warn('Could not get result count from parent component:', error);
      return 0;
    }
  }
}
